import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, Phone, MapPin, Calendar, Check } from 'lucide-react';
import { toast } from 'sonner';
import { FadeUp, TextReveal } from '@/components/site/Reveal';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { submitLead, fetchFaqs, STATIC } from '@/lib/cms';
import { useSettings } from '@/lib/settings';
import SEO from '@/components/site/SEO';

export default function Contact() {
  const settings = useSettings();
  const [form, setForm] = useState({ name: '', email: '', company: '', budget: '$25k+', service: 'Web Design', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [faqs, setFaqs] = useState(STATIC.faqs);
  useEffect(() => { fetchFaqs().then(setFaqs); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { ok } = await submitLead(form);
    setLoading(false);
    if (ok) { setDone(true); toast.success('Thanks — we\'ll be in touch within 24 hours.'); setForm({ name: '', email: '', company: '', budget: '$25k+', service: 'Web Design', message: '' }); }
    else toast.error('Something went wrong. Email us at hello@slinkflow.com');
  };

  return (
    <>
      <SEO title="Contact — Slinkflow" description="Get in touch with Slinkflow to start your project." />
      <section className="relative overflow-hidden pt-40 pb-16 lg:pt-56 lg:pb-24">
        <div className="absolute -top-32 right-0 h-[520px] w-[520px] hero-gradient-orb" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">Contact</p>
          <h1 className="mt-6 max-w-5xl font-display text-[56px] font-light leading-[1.02] tracking-[-0.03em] md:text-[88px] lg:text-[112px]">
            <TextReveal text="Let's start" />
            <br />
            <span className="gradient-text font-medium"><TextReveal text="the conversation." delay={0.2} /></span>
          </h1>
          <p className="mt-6 max-w-2xl text-[18px] text-ink-500">Tell us about your project. We&apos;ll reply within 24 hours with a clear next step.</p>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 md:px-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-ink-100 bg-white p-8 md:p-10">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Your name" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="contact-name" required />
                <Field label="Email" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} testid="contact-email" required />
                <Field label="Company" value={form.company} onChange={v => setForm({ ...form, company: v })} testid="contact-company" />
                <Select label="Service" value={form.service} options={['Web Design','Web Development','Brand Identity','SEO & Growth','Mobile Apps','E-commerce','Not sure yet']} onChange={v => setForm({ ...form, service: v })} testid="contact-service" />
                <Select label="Budget" value={form.budget} options={['<$10k','$10k–$25k','$25k+','$50k+','$100k+']} onChange={v => setForm({ ...form, budget: v })} testid="contact-budget" />
              </div>
              <div className="mt-5">
                <label className="block text-xs uppercase tracking-[0.2em] text-ink-400 mb-2">Project details</label>
                <textarea data-testid="contact-message" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full resize-none rounded-2xl border border-ink-100 bg-ink-50/50 px-5 py-4 text-[15px] focus:border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/20" placeholder="A bit about your goals, timeline and any links we should see." />
              </div>
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-ink-500">By submitting, you agree to our <Link to="/privacy" className="link-underline">privacy policy</Link>.</p>
                <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-cyan inline-flex items-center gap-2 rounded-full px-7 py-4 text-[15px] font-medium disabled:opacity-50">
                  {loading ? 'Sending…' : 'Send message'} <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
              {done && (
                <div className="mt-6 flex items-center gap-2 rounded-2xl bg-cyan-soft px-5 py-4 text-sm text-cyan-700">
                  <Check className="h-4 w-4" /> Got it. Expect a reply within 24 hours.
                </div>
              )}
            </form>
          </FadeUp>

          <div className="lg:col-span-5 space-y-6">
            <FadeUp delay={0.1}>
              <div className="rounded-3xl bg-ink-900 p-8 text-white">
                <p className="text-xs uppercase tracking-[0.3em] text-cyan-400">Prefer a call?</p>
                <h3 className="mt-3 font-display text-3xl">Book a free 30-min strategy call.</h3>
                <p className="mt-3 text-sm text-white/70">No pitch. Three specific wins you can implement this quarter.</p>
                <a href="#calendly" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm text-ink-900">
                  <Calendar className="h-4 w-4" /> Book on Calendly
                </a>
              </div>
            </FadeUp>
            <FadeUp delay={0.15}>
              <div className="rounded-3xl border border-ink-100 bg-white p-8 space-y-4 text-[15px]">
                <Info icon={Mail} label="Email" value={settings.email} href={`mailto:${settings.email}`} />
                {settings.phone && <Info icon={Phone} label="Phone (India)" value={settings.phone} href={`tel:${settings.phone.replace(/[^+\d]/g,'')}`} />}
                {settings.phone_secondary && <Info icon={Phone} label="Phone (US)" value={settings.phone_secondary} href={`tel:${settings.phone_secondary.replace(/[^+\d]/g,'')}`} />}
                <Info icon={MapPin} label="Office" value={settings.address} />
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="overflow-hidden rounded-3xl">
                <iframe title="Slinkflow office location" src={`https://www.google.com/maps?q=${encodeURIComponent(settings.map_query || settings.address || 'Surat, Gujarat')}&output=embed`} className="h-64 w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="bg-ink-50 py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan">FAQ</p>
            <h2 className="mt-4 font-display text-[40px] font-light leading-tight">Before you <span className="gradient-text font-medium">message us.</span></h2>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="divide-y divide-ink-200 border-y border-ink-200">
              {faqs.map((f, i) => (
                <AccordionItem key={f.id} value={`item-${i}`} className="border-0">
                  <AccordionTrigger className="py-6 text-left text-[18px] hover:text-cyan hover:no-underline">{f.question}</AccordionTrigger>
                  <AccordionContent className="pb-6 text-ink-500">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-ink-400 mb-2">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-2xl border border-ink-100 bg-ink-50/50 px-5 py-3.5 text-[15px] focus:border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/20" />
    </div>
  );
}
function Select({ label, value, options, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-ink-400 mb-2">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-2xl border border-ink-100 bg-ink-50/50 px-5 py-3.5 text-[15px] focus:border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/20">
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
function Info({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-center gap-4">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-cyan-soft text-cyan"><Icon className="h-4 w-4" /></div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-ink-400">{label}</p>
        <p className="mt-0.5 text-ink-900">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:text-cyan">{inner}</a> : inner;
}
